/**
 * Simba Herbals → hpanel payment API
 * Set REACT_APP_HPANEL_API_URL in .env (e.g. https://your-hpanel.com/api)
 */

import type { CartItem } from '../context/CartContext';

const BASE = process.env.REACT_APP_HPANEL_API_URL || '';

const PAYMENT_SERVER_ERROR =
  'Payment server not configured. Add REACT_APP_HPANEL_API_URL to .env pointing to your backend (e.g. https://your-api.com/api).';

function ensureBaseUrl(): void {
  if (!BASE || !BASE.startsWith('http')) {
    throw new Error(PAYMENT_SERVER_ERROR);
  }
}

async function parseJsonResponse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const trimmed = text.trim();
  if (trimmed.startsWith('<')) {
    throw new Error(
      'Server returned a page instead of JSON. Check REACT_APP_HPANEL_API_URL in .env and that your payment backend is running.'
    );
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Invalid response from payment server.');
  }
}

export interface CreatePaymentIntentRequest {
  amount: number; // in GBP, e.g. 19.99
  currency?: string; // default GBP
  lineItems: { id: number; name: string; price: number; quantity: number }[];
}

export interface CreatePaymentIntentResponse {
  clientSecret?: string;
  client_secret?: string;
  paymentIntentId?: string;
  payment_intent_id?: string;
}

export interface ShippingAddress {
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
  country: string; // e.g. GB, US
  countryName?: string;
}

export interface RecordOrderRequest {
  paymentMethod: 'stripe' | 'paypal';
  paymentId: string; // Stripe paymentIntentId or PayPal orderId
  amount: number; // order total including shipping
  currency: string;
  lineItems: CartItem[];
  shippingOption?: 'local' | 'international';
  shippingCost?: number;
  shippingAddress?: ShippingAddress;
  customerEmail?: string; // for owner to contact customer and for tracking lookup
}

export interface RecordOrderResponse {
  success: boolean;
  orderId?: string;
  message?: string;
}

export async function createPaymentIntent(
  body: CreatePaymentIntentRequest
): Promise<CreatePaymentIntentResponse> {
  ensureBaseUrl();
  const res = await fetch(`${BASE}/payments/create-payment-intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      amount: Math.round(body.amount * 100) / 100,
      currency: body.currency || 'GBP',
      line_items: body.lineItems,
    }),
  });
  const data = await parseJsonResponse<CreatePaymentIntentResponse & { message?: string }>(res);
  if (!res.ok) {
    throw new Error(data.message || 'Failed to create payment');
  }
  return {
    clientSecret: data.clientSecret ?? data.client_secret,
    paymentIntentId: data.paymentIntentId ?? data.payment_intent_id,
  };
}

export async function recordOrder(body: RecordOrderRequest): Promise<RecordOrderResponse> {
  ensureBaseUrl();
  const res = await fetch(`${BASE}/payments/record-order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      payment_method: body.paymentMethod,
      payment_id: body.paymentId,
      amount: body.amount,
      currency: body.currency,
      line_items: body.lineItems,
      shipping_option: body.shippingOption,
      shipping_cost: body.shippingCost,
      shipping_address: body.shippingAddress,
      customer_email: body.customerEmail,
    }),
  });
  const data = await parseJsonResponse<RecordOrderResponse>(res);
  if (!res.ok) {
    throw new Error(data.message || 'Failed to record order');
  }
  return data;
}

/** Order lookup for tracking – backend returns status and tracking if available */
export interface OrderLookupResponse {
  found: boolean;
  orderId?: string;
  status?: 'pending' | 'shipped' | 'delivered';
  trackingNumber?: string;
  trackingUrl?: string;
  carrier?: string;
  message?: string;
}

export async function lookupOrder(orderId: string, email: string): Promise<OrderLookupResponse> {
  if (!BASE || !BASE.startsWith('http')) {
    return { found: false, message: 'Order tracking is not set up yet. Contact us with your order reference.' };
  }
  const params = new URLSearchParams({ order_id: orderId.trim(), email: email.trim() });
  const res = await fetch(`${BASE}/orders/lookup?${params}`);
  const data = await parseJsonResponse<OrderLookupResponse>(res);
  return data;
}
